import { useCallback, useState, type FormEvent } from 'react'
import { Link, useParams } from 'react-router-dom'

import {
  Badge,
  Card,
  EmptyState,
  ErrorNote,
  PageHeader,
  Spinner,
  StatusBadge,
  cx,
} from '@/components/ui'
import { useQuery } from '@/hooks/useQuery'
import { ApiError, api } from '@/lib/api'
import { dateTime, relativeTime, sentence, timeOnly } from '@/lib/format'
import type { Ack, ConversationDetail, Extraction, Message } from '@/types'

export function ConversationDetailPage() {
  const { conversationId } = useParams<{ conversationId: string }>()
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const query = useQuery(
    useCallback(
      () => api.get<ConversationDetail>(`/conversations/${conversationId}`),
      [conversationId],
    ),
    [conversationId],
  )

  async function send(event: FormEvent) {
    event.preventDefault()
    if (!draft.trim()) return
    setBusy(true)
    setError(null)
    try {
      await api.post(`/conversations/${conversationId}/messages`, { body: draft.trim() })
      setDraft('')
      query.reload()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The message could not be sent.')
    } finally {
      setBusy(false)
    }
  }

  async function control(action: 'pause' | 'resume' | 'complete' | 'extract') {
    setBusy(true)
    setError(null)
    try {
      await api.post<Ack>(`/conversations/${conversationId}/${action}`)
      query.reload()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That action failed.')
    } finally {
      setBusy(false)
    }
  }

  if (query.loading && !query.data) return <Spinner label="Loading conversation" />
  if (query.error) return <ErrorNote message={query.error} onRetry={query.reload} />
  if (!query.data) return null

  const conversation = query.data
  const closed =
    conversation.status === 'opted_out' || conversation.status === 'completed'

  return (
    <>
      <PageHeader
        title={conversation.contact.display_name || conversation.contact.phone_e164}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <StatusBadge status={conversation.status} />
            <span className="text-ink-muted">
              {sentence(conversation.goal)} · {conversation.turn_count} turns
            </span>
            <Link
              to={`/contacts/${conversation.contact_id}`}
              className="text-ink-muted hover:text-ink"
            >
              View profile →
            </Link>
          </span>
        }
        action={
          <div className="flex flex-wrap gap-2">
            {conversation.status === 'paused' ? (
              <button
                type="button"
                className="btn-secondary"
                disabled={busy}
                onClick={() => control('resume')}
              >
                Resume agent
              </button>
            ) : (
              !closed && (
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={busy}
                  onClick={() => control('pause')}
                >
                  Pause agent
                </button>
              )
            )}
            <button
              type="button"
              className="btn-secondary"
              disabled={busy}
              onClick={() => control('extract')}
            >
              Re-extract
            </button>
          </div>
        }
      />

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Card className="flex flex-col xl:col-span-2" title="Messages">
          <div className="max-h-[34rem] flex-1 space-y-3 overflow-y-auto px-4 py-4">
            {conversation.messages.length === 0 ? (
              <EmptyState title="No messages yet" />
            ) : (
              conversation.messages.map((message) => (
                <Bubble key={message.id} message={message} />
              ))
            )}
          </div>

          <form onSubmit={send} className="border-t border-line px-4 py-3">
            {closed ? (
              <p className="text-xs text-ink-muted">
                {conversation.status === 'opted_out'
                  ? 'This person has opted out. No further messages can be sent.'
                  : 'This conversation is closed.'}
              </p>
            ) : (
              <>
                <div className="flex gap-2">
                  <input
                    type="text"
                    className="field flex-1"
                    placeholder="Write a message as the concierge…"
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    maxLength={4000}
                  />
                  <button type="submit" className="btn-primary" disabled={busy || !draft.trim()}>
                    Send
                  </button>
                </div>
                <p className="mt-1.5 text-xs text-ink-faint">
                  Sending here pauses nothing — the agent still replies to the next inbound
                  message unless you pause it.
                </p>
              </>
            )}
            {error && (
              <p className="mt-2 text-xs" style={{ color: 'var(--critical)' }} role="alert">
                {error}
              </p>
            )}
          </form>
        </Card>

        <div className="space-y-4">
          <Card title="Agent reasoning" subtitle="Why it asked what it asked">
            <dl className="space-y-2.5 px-4 py-4 text-sm">
              <Item
                label="Last reasoning"
                value={String(conversation.agent_state.last_reasoning ?? '—')}
              />
              <Item
                label="Opportunity noticed"
                value={String(conversation.agent_state.last_opportunity_hint || '—')}
              />
              <div>
                <dt className="text-xs text-ink-faint">Topics already covered</dt>
                <dd className="mt-1 flex flex-wrap gap-1.5">
                  {(conversation.agent_state.asked_topics as string[] | undefined)?.length ? (
                    (conversation.agent_state.asked_topics as string[]).map((topic) => (
                      <Badge key={topic}>{sentence(topic)}</Badge>
                    ))
                  ) : (
                    <span className="text-sm text-ink-faint">None yet</span>
                  )}
                </dd>
              </div>
              {conversation.agent_state.ai_fallback === true && (
                <Badge tone="warning" icon="⚠">
                  Last turn used the deterministic fallback
                </Badge>
              )}
            </dl>
          </Card>

          <Card
            title="Extractions"
            subtitle="What the model proposed, and what survived validation"
          >
            {conversation.extractions.length === 0 ? (
              <EmptyState title="No extraction runs yet" />
            ) : (
              <ul className="divide-y divide-line">
                {conversation.extractions.map((extraction) => (
                  <ExtractionRow key={extraction.id} extraction={extraction} />
                ))}
              </ul>
            )}
          </Card>

          <Card title="Session">
            <dl className="space-y-2 px-4 py-4 text-sm">
              <Item label="Started" value={dateTime(conversation.started_at)} />
              <Item label="Last reply" value={relativeTime(conversation.last_inbound_at)} />
              <Item
                label="Window closes"
                value={
                  conversation.window_expires_at
                    ? dateTime(conversation.window_expires_at)
                    : 'Not open'
                }
              />
              <Item label="Messages" value={String(conversation.message_count)} />
            </dl>
          </Card>
        </div>
      </div>
    </>
  )
}

function Bubble({ message }: { message: Message }) {
  const inbound = message.direction === 'inbound'
  return (
    <div className={cx('flex', inbound ? 'justify-start' : 'justify-end')}>
      <div className={cx('max-w-[80%]', inbound ? 'items-start' : 'items-end')}>
        <div
          className="rounded-lg px-3 py-2 text-sm"
          style={{
            background: inbound ? 'var(--surface-2)' : 'var(--wash)',
            color: 'var(--ink-1)',
          }}
        >
          {message.body || <span className="italic text-ink-faint">({message.type})</span>}
        </div>
        <div
          className={cx(
            'mt-1 flex items-center gap-1.5 text-xs text-ink-faint',
            inbound ? 'justify-start' : 'justify-end',
          )}
        >
          <span>{timeOnly(message.created_at)}</span>
          {message.ai_generated && <span title="Written by the conversation agent">· AI</span>}
          {message.template_name && <span>· template</span>}
          {!inbound && <span>· {message.status}</span>}
          {message.status === 'failed' && (
            <span style={{ color: 'var(--critical)' }} title={JSON.stringify(message.error)}>
              ⚠
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

function ExtractionRow({ extraction }: { extraction: Extraction }) {
  const [open, setOpen] = useState(false)
  return (
    <li className="px-4 py-2.5">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 text-left"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
      >
        <span>
          <span className="block text-sm text-ink">
            {extraction.accepted_count} accepted · {extraction.rejected_count} rejected
          </span>
          <span className="block text-xs text-ink-faint">
            {relativeTime(extraction.created_at)} · {extraction.model} v
            {extraction.prompt_version}
          </span>
        </span>
        <span className="text-xs text-ink-faint">{open ? '▴' : '▾'}</span>
      </button>

      {open && (
        <div className="mt-2 space-y-2 rounded-md px-3 py-2 text-xs" style={{ background: 'var(--surface-2)' }}>
          {extraction.error && (
            <p style={{ color: 'var(--critical)' }}>Failed: {extraction.error}</p>
          )}
          {Array.isArray(extraction.normalised.accepted) &&
            (extraction.normalised.accepted as string[]).length > 0 && (
              <div>
                <p className="mb-1 font-medium text-ink">Accepted</p>
                <ul className="space-y-0.5 text-ink-muted">
                  {(extraction.normalised.accepted as string[]).map((line, index) => (
                    <li key={index}>· {line}</li>
                  ))}
                </ul>
              </div>
            )}
          {extraction.rejection_reasons.length > 0 && (
            <div>
              <p className="mb-1 font-medium text-ink">
                Rejected by validation
                <span className="ml-1 font-normal text-ink-faint">
                  — the merger refused these
                </span>
              </p>
              <ul className="space-y-0.5 text-ink-muted">
                {extraction.rejection_reasons.map((reason, index) => (
                  <li key={index}>· {reason}</li>
                ))}
              </ul>
            </div>
          )}
          <p className="text-ink-faint">
            {extraction.prompt_tokens + extraction.completion_tokens} tokens ·{' '}
            {extraction.latency_ms}ms
          </p>
        </div>
      )}
    </li>
  )
}

function Item({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-ink-faint">{label}</dt>
      <dd className="mt-0.5 text-ink">{value}</dd>
    </div>
  )
}
